import { Button } from "@twilio-paste/button";
import { Box } from "@twilio-paste/core";
import { HelpText } from "@twilio-paste/help-text";
import { useState } from "react";

import { getToken, getTokenFromTwilio } from "../../services/api";
import styles from "../../styles";
import { InputType } from "../../types";
import BhioLogo from "../icons/BhioLogo";
import { SearchableSelect } from "../SearchableSelect";
import ModalInputField from "../modals/ModalInputField";

type SetTokenType = (token: string) => void;

interface LoginProps {
  setToken: SetTokenType;
}

const Login: React.FC<LoginProps> = (props: LoginProps) => {
  const [isFormDirty, setFormDirty] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(1);
  const [partner, setPartner] = useState("");
  const [userToken, setUserToken] = useState("");
  const [partners, setPartners] = useState("".split(",") ?? []);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isPartnerLoginLoading, setIsPartnerLoginLoading] = useState(false);

  async function login(username: string, password: string): Promise<string> {
    try {
      await getToken(username.trim(), password);

      localStorage.setItem("username", username);
      localStorage.setItem("password", password);
      const partners = localStorage.getItem("partners")?.split(",") ?? [];
      setPartners(partners);
      setFormError("");
      setStep(2);
      return "";
    } catch (error) {
      return error as string;
    }
  }
  async function goToHome(setToken: (token: string) => void) {
    const name = localStorage.getItem("name") ?? "";
    localStorage.setItem("partner", partner);

    try {
      const token = await getTokenFromTwilio(username.trim(), partner, name);
      if (token === "") {
        return "Algo deu errado. Tente novamente.";
      }
      setUserToken(token);
      setToken(token);
      setFormError("");

      return "";
    } catch (error) {
      return error as string;
    }
  }

  async function handleLogin() {
    if (isLoginLoading) {
      return;
    }

    setIsLoginLoading(true);

    const error = await login(username, password);
    if (error) {
      setFormError(error);
      setTimeout(() => {
        setFormError("");
      }, 3000);
    }

    setIsLoginLoading(false);
  }

  function handleLoginKeyDown(event: React.KeyboardEvent) {
    const isEnterKey = event.key === "Enter";
    if (!isEnterKey || !username || !password) {
      return;
    }

    event.preventDefault();
    void handleLogin();
  }

  async function handlePartnerLogin() {
    if (isPartnerLoginLoading) {
      return;
    }

    setIsPartnerLoginLoading(true);

    const error = await goToHome(props.setToken);
    if (error) {
      setFormError(error);
      setTimeout(() => {
        setFormError("");
      }, 3000);
    }

    setIsPartnerLoginLoading(false);
  }

  return (
    <Box style={styles.loginContainer}>
      <Box style={styles.loginContent}>
        <Box style={{ filter: "brightness(0) invert(1)" }}>
          <BhioLogo />
        </Box>
        <div style={styles.loginTitle}>Bhiotrack Conversas</div>
        <div style={styles.subTitle}></div>
        {step === 1 ? (
          <Box style={styles.loginForm} onKeyDown={handleLoginKeyDown}>
            <Box style={styles.userInput}>
              <ModalInputField
                label="Nome de usuário"
                placeholder=""
                isFocused={true}
                error={
                  isFormDirty && !username.trim()
                    ? "Insira um nome de usuário para entrar."
                    : ""
                }
                input={username}
                onBlur={() => setFormDirty(true)}
                onChange={setUsername}
                id="username"
              />
            </Box>
            <Box style={styles.passwordInput}>
              <ModalInputField
                label="Senha"
                placeholder=""
                error={
                  isFormDirty && !password
                    ? "Insira uma senha para entrar."
                    : formError ?? ""
                }
                input={password}
                onChange={setPassword}
                inputType={showPassword ? InputType.Text : InputType.Password}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                id="password"
              />
            </Box>
            <Box style={styles.loginButton}>
              <Button
                fullWidth
                disabled={!username || !password || isLoginLoading}
                variant="primary"
                onClick={handleLogin}
                id="login"
              >
                {isLoginLoading ? "Entrando..." : "Entrar"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box style={styles.loginForm}>
            <Box style={styles.userInput}>
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Selecione o parceiro
              </label>
              <SearchableSelect
                id="numproxies"
                options={partners ?? []}
                value={partner}
                onChange={setPartner}
                placeholder="Selecione"
                width="100%"
              />
              {formError && (
                <HelpText id="error_help_text" variant="error">
                  {formError}
                </HelpText>
              )}
            </Box>
            <Box style={styles.loginButton}>
              <Button
                fullWidth
                disabled={!partner || isPartnerLoginLoading}
                variant="primary"
                onClick={handlePartnerLogin}
                id="partner-login"
              >
                {isPartnerLoginLoading ? "Acessando..." : "Acessar"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Box style={styles.loginBackground}>
        <Box
          style={{
            height: "100%",
            width: "100%",
            backgroundColor: "#06033a",
            transform: "skewY(-12deg)",
            transformOrigin: "top right",
          }}
        />
      </Box>
    </Box>
  );
};

export default Login;
